import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@tyro/i18n';
import { CloudUploadIcon } from '@tyro/icons';
import { type DropzoneOptions, useDropzone } from 'react-dropzone';
import { useFileErrorMessage } from '../../hooks';

type FileUploaderProps = {
  multiple?: boolean;
  onUpload: (files: File[]) => void;
  uploading: boolean;
  maxFileSizeInKiloBytes?: number;
  DropzoneOptions?: Partial<DropzoneOptions>;
};

export function getFileSizeLabel(sizeInKiloByte: number) {
  if (sizeInKiloByte < 1024) {
    return `${sizeInKiloByte} KB`;
  }

  if (sizeInKiloByte < 1024 * 1024) {
    return `${Math.round(sizeInKiloByte / 1024)} MB`;
  }

  return `${Math.round(sizeInKiloByte / (1024 * 1024))} GB`;
}

export const FileDropzone = ({
  multiple = false,
  onUpload,
  uploading,
  maxFileSizeInKiloBytes = 1024 * 10,
  DropzoneOptions,
}: FileUploaderProps) => {
  const { t } = useTranslation(['common']);

  const { showErrorMessage } = useFileErrorMessage();
  const unlimited = DropzoneOptions?.maxSize === Number.POSITIVE_INFINITY;
  const maxFileSizeLabel = getFileSizeLabel(maxFileSizeInKiloBytes);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const { code } = rejectedFiles[0].errors[0];
        showErrorMessage(code, { size: maxFileSizeLabel });
        return;
      }

      onUpload(acceptedFiles);
    },
    multiple,
    maxSize: maxFileSizeInKiloBytes * 1024,
    onError: (error) => {
      console.error(error);
    },
    ...DropzoneOptions,
  });

  const dragDropStrings = multiple
    ? {
        header: t('common:uploadFiles'),
        inactive: unlimited
          ? t('common:dragNDropFilesHereNoLimit')
          : t('common:dragNDropFilesHere', { maxSize: maxFileSizeLabel }),
        active: t('common:dropTheFilesHere'),
      }
    : {
        header: t('common:uploadFile'),
        inactive: unlimited
          ? t('common:dragNDropFileHereNoLimit')
          : t('common:dragNDropFileHere', { maxSize: maxFileSizeLabel }),
        active: t('common:dropTheFileHere'),
      };

  const dragMessage = isDragActive
    ? dragDropStrings.active
    : dragDropStrings.inactive;

  const uploaderSx = {
    backgroundColor: 'slate.50',
    cursor: 'pointer',
    height: 130,
    borderColor: 'slate.300',
    borderStyle: 'dashed',
    borderRadius: 1.5,
    borderWidth: 1,
    p: 1,
  };

  return uploading ? (
    <Stack alignItems="center" justifyContent="center" sx={uploaderSx}>
      <Typography variant="h4">{t('common:uploading')}</Typography>
    </Stack>
  ) : (
    <Stack
      {...getRootProps()}
      alignItems="center"
      justifyContent="center"
      sx={uploaderSx}
    >
      <input {...getInputProps()} />
      <Typography variant="h4">{dragDropStrings.header}</Typography>
      <Stack direction="row" alignItems="center" sx={{ color: 'slate.500' }}>
        <CloudUploadIcon />
        <Typography sx={{ ml: 1, textWrap: 'pretty' }}>
          {dragMessage}
        </Typography>
      </Stack>
    </Stack>
  );
};
